import * as constants from './constants'

import { postAsJson, fetchAsJson } from '@eig-builder/core-utils/helpers/fetch-helper'
import { api } from '@eig-builder/core-utils/helpers/url-helper'
import { camelToSnakeCaseObject } from '@eig-builder/core-utils/helpers/object-helper'
import map from 'lodash/map'
import isNil from 'lodash/isNil'

import { withInstanceId, isGuid, isString } from './utils'

export const addProductToCart = (productId, skuId) => ({
  type: constants.ADD_PRODUCT_TO_CART,
  body: {
    productId,
    skuId
  }
})

export const updateProductQuantity = (productId, skuId, quantity) => ({
  type: constants.UPDATE_PRODUCT_QUANTITY,
  body: {
    productId,
    skuId,
    quantity
  }
})

export const updateFulfillmentMethod = (productId, skuId, methodId) => ({
  type: constants.UPDATE_FULFILLMENT_METHOD,
  body: {
    productId,
    skuId,
    methodId
  }
})

export const removeProductFromCart = (productId, skuId) => ({
  type: constants.REMOVE_PRODUCT_FROM_CART,
  body: {
    productId,
    skuId
  }
})

export const clearProductFromCart = (productId, skuId) => ({
  type: constants.CLEAR_PRODUCT_FROM_CART,
  body: {
    productId,
    skuId
  }
})

export const getProductInfoFromServer = (items, cartHash) => {
  return postAsJson(
    withInstanceId(api('/v1.0/cart/products', '')),
    camelToSnakeCaseObject(items),
    {
      key: constants.GET_PRODUCT_INFO_FROM_SERVER,
      extraArguments: {
        cartHash
      }
    }
  )
}

export const startCheckoutSession = (
  items,
  discountCodes,
  isTestMode,
  insightsTrackingId,
  insightsUserId,
  insightsSessionId,
  customThankYou,
  cartClickTrack
) => {
  const mappedProducts = map(items, ({ productId, quantity, propertyBag, fulfillmentMethodSelected }) => {
    const obj = {}
    obj.quantity = quantity
    obj.productId = productId
    if (!isNil(fulfillmentMethodSelected)) {
      obj.fulfillmentType = fulfillmentMethodSelected
    }
    // propertyBag must be an object and conform to schema
    if (propertyBag && typeof propertyBag === 'object' && !Array.isArray(propertyBag)) {
      const enforcedFields = ['ab_campaign_activity_id', 'lp_campaign_activity_id', 'lp_identifier']
      const optionalFields = ['source_id', 'source_type']
      const allValidFields = enforcedFields.concat(optionalFields)
      const fieldTypes = {
        ab_campaign_activity_id: 'uuid',
        lp_campaign_activity_id: 'uuid',
        lp_identifier: 'string',
        source_id: 'string',
        source_type: 'string'
      }
      const validators = {
        uuid: isGuid,
        string: isString
      }
      let valid = true
      // enforce required fields as valid, otherwise fail validation
      enforcedFields.forEach(field => {
        const type = fieldTypes[field]
        const validator = validators[type]
        const isValidType = validator(propertyBag[field])
        if (!isValidType) {
          valid = false
        }
      })
      // enforce no invalid fields
      for (const key in propertyBag) {
        if (!allValidFields.includes(key)) {
          valid = false
        }
      }
      if (valid) {
        obj.shoppableLandingPageTrackingInfo = {}
        // always send optional fields with values if valid or otherwise as null
        allValidFields.forEach(field => {
          const type = fieldTypes[field]
          const validator = validators[type]
          const isValidType = validator(propertyBag[field])
          obj.shoppableLandingPageTrackingInfo[field] = isValidType ? propertyBag[field] : null
        })
      }
    }
    return camelToSnakeCaseObject(obj)
  })
  const body = {
    products: mappedProducts, // todo support variants
    discount_codes: discountCodes,
    timestamp: new Date().toJSON(),
    is_test_order: isTestMode || false,
    insights_tracking_id: insightsTrackingId,
    insights_user_id: insightsUserId,
    insights_session_id: insightsSessionId
  }

  if (customThankYou) {
    body.custom_thank_you = customThankYou
  }

  if (cartClickTrack) {
    const fieldTypes = {
      lpCampaignActivityId: 'uuid',
      sourceId: 'uuid',
      sourceType: 'string',
      contactId: 'uuid'
    }
    const validators = {
      uuid: isGuid,
      string: isString
    }
    const alwaysSentFields = Object.keys(fieldTypes)
    const enforcedFields = ['lpCampaignActivityId']

    const cartClickPayload = {}
    // control fields always attached to payload.  Never any unwhitelisted fields.
    alwaysSentFields.forEach((field) => {
      const type = fieldTypes[field]
      const isValid = validators[type]
      cartClickPayload[field] = isValid(cartClickTrack[field]) ? cartClickTrack[field] : null
    })

    // make sure required fields are attached and valid
    let isValidPayload = true
    enforcedFields.forEach(field => {
      if (!cartClickPayload[field]) {
        isValidPayload = false
      }
    })
    if (isValidPayload) {
      body.ctct_slp_cart_click_track = camelToSnakeCaseObject(cartClickPayload)
    }
  }

  return postAsJson(
    withInstanceId(api('/v2.0/checkout/create')),
    body,
    {
      key: constants.CREATE_CHECK_OUT_SESSION
    }
  )
}

export const resetCheckoutUrl = () => ({
  type: constants.RESET_CHECK_OUT_SESSION,
  body: null
})

export const getStoreSettings = () => {
  return fetchAsJson(withInstanceId(api('/v1.0/settings')), {
    key: constants.GET_STORE_SETTINGS
  })
}

export const checkDiscountCode = discountCode => {
  const encodedDiscountCode = encodeURIComponent(discountCode)
  return fetchAsJson(
    withInstanceId(api(
      `/v1.0/discounts/validate?currentDateTime=${new Date().toJSON()}&couponCode=${encodedDiscountCode}`
    )),
    {
      key: constants.CHECK_DISCOUNT_CODE
    }
  )
}

export const clearDiscountCode = () => ({
  type: constants.CLEAR_DISCOUNT_CODE,
  body: null
})

export const setCheckoutWindow = window => ({
  type: constants.SET_CHECKOUT_WINDOW,
  body: window
})

export const clearDiscounts = () => ({
  type: constants.CLEAR_DISCOUNTS,
  body: null
})

export const setDiscounts = discounts => ({
  type: constants.SET_DISCOUNTS,
  body: discounts
})
